import { useEffect, useState } from 'react';
import { checkPushPermission, enablePushOnDevice } from '../lib/push';
import { Modal } from './Modal';

const STORAGE_KEY = 'la-push-prompt-dismissed-at';
/** Re-show the rationale at most once every ~30 days if the user dismissed. */
const REDISPLAY_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Pre-flight rationale before the OS push-permission dialog.
 *
 * The OS prompt itself is one line ("LessAlone wants to send you
 * notifications") with two buttons. Acceptance rates climb when an app
 * shows a short branded explainer first so the system prompt feels
 * expected. This modal:
 *
 *  - Only renders on a native platform whose permission state is still
 *    'prompt'. On the web SPA, on already-granted, or on already-denied,
 *    it stays out of the way.
 *  - Stores the dismissal timestamp in localStorage so a user who said
 *    "Not now" isn't pestered every navigation; the modal re-surfaces
 *    after REDISPLAY_AFTER_MS so a long-running install can be invited
 *    again later.
 *  - On accept, calls enablePushOnDevice() which triggers the OS dialog
 *    and registers the FCM token on grant.
 */
export function PushPermissionPrompt() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const state = await checkPushPermission();
      if (cancelled) return;
      if (state !== 'prompt') return;
      const dismissedAtRaw = window.localStorage.getItem(STORAGE_KEY);
      const dismissedAt = dismissedAtRaw ? Number(dismissedAtRaw) : 0;
      if (Number.isFinite(dismissedAt) && Date.now() - dismissedAt < REDISPLAY_AFTER_MS) return;
      setOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function dismiss() {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage may be unavailable in private mode — falling through means
      // we'll just ask again next launch, which is fine.
    }
    setOpen(false);
  }

  async function accept() {
    setBusy(true);
    try {
      await enablePushOnDevice();
    } finally {
      setBusy(false);
      // Either granted, denied, or unavailable — close either way so the
      // rationale doesn't linger on top of the OS dialog's resolution.
      // Record the timestamp so 'denied' doesn't re-pop on every nav.
      try {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {
        // ignore
      }
      setOpen(false);
    }
  }

  if (!open) return null;
  return (
    <Modal title="Get notified when a match writes?" onClose={dismiss}>
      <p className="muted">
        LessAlone messages are async, so a reply can sit unseen for hours. A quiet ping on your
        phone is the bridge — only when there's something real waiting.
      </p>
      <ul className="agreement-list">
        <li>
          <span className="agreement-eyebrow">Only for real activity.</span>
          <span>Messages from your match, the 48-hour nudge — never marketing or noise.</span>
        </li>
        <li>
          <span className="agreement-eyebrow">Lock-screen privacy.</span>
          <span>The push says a message is waiting; it never shows what was said.</span>
        </li>
        <li>
          <span className="agreement-eyebrow">Always reversible.</span>
          <span>Mute any channel from Settings → Notifications.</span>
        </li>
      </ul>
      <div className="modal-actions">
        <button type="button" className="secondary" onClick={dismiss} disabled={busy}>
          Not now
        </button>
        <button type="button" onClick={() => void accept()} disabled={busy}>
          {busy ? 'Asking…' : 'Allow on this device'}
        </button>
      </div>
    </Modal>
  );
}
